// ToolRegistry — flat tool registry with pre-built embedding index for RAG search

function cosineSimilarity(a, b) {
  let dot = 0, magA = 0, magB = 0
  for (let i = 0; i < a.length; i++) {
    dot += a[i] * b[i]
    magA += a[i] * a[i]
    magB += b[i] * b[i]
  }
  magA = Math.sqrt(magA)
  magB = Math.sqrt(magB)
  return (magA && magB) ? dot / (magA * magB) : 0
}

export class ToolRegistry {
  constructor({ embedFn, queryPrefix = '', docPrefix = '' }) {
    this.tools = new Map()
    this.embedFn = embedFn
    this.queryPrefix = queryPrefix
    this.docPrefix = docPrefix
    this.index = null
    this._buildPromise = null
  }

  register(entry) {
    this.tools.set(entry.id, entry)
  }

  registerBatch(entries) {
    for (const entry of entries) {
      this.tools.set(entry.id, entry)
    }
  }

  unregister(id) {
    this.tools.delete(id)
  }

  unregisterBySource(source) {
    for (const [id, entry] of this.tools) {
      if (entry.source === source) this.tools.delete(id)
    }
  }

  async buildIndex() {
    const promise = this._doBuildIndex()
    this._buildPromise = promise
    await promise
    this._buildPromise = null
  }

  async _doBuildIndex() {
    const entries = [...this.tools.values()]
    if (!entries.length) {
      this.index = { entries: [], embeddings: [] }
      return
    }

    const docs = entries.map(e => {
      const parts = [e.searchText, e.description]
      if (e.examples?.length) {
        parts.push(...e.examples.map(ex => ex.input))
      }
      if (e.parameters?.properties) {
        parts.push(...Object.entries(e.parameters.properties)
          .map(([k, v]) => `${k}: ${v.description || ''}`)
        )
      }
      const text = parts.filter(Boolean).join('\n')
      return this.docPrefix ? `${this.docPrefix}${text}` : text
    })

    const embeddings = await this.embedFn(docs)
    this.index = { entries, embeddings }
  }

  async search(query, { limit = 5, minScore = 0.3 } = {}) {
    if (this._buildPromise) await this._buildPromise
    if (!this.index) {
      throw new Error('Index not built. Call buildIndex() first.')
    }

    const q = this.queryPrefix ? `${this.queryPrefix}${query}` : query
    const [queryVector] = await this.embedFn([q])

    return this.index.entries
      .map((entry, i) => ({
        entry,
        score: cosineSimilarity(queryVector, this.index.embeddings[i]),
      }))
      .filter(r => r.score >= minScore)
      .sort((a, b) => b.score - a.score)
      .slice(0, limit)
  }

  get(id) {
    const entry = this.tools.get(id)
    if (!entry) throw new Error(`Tool not found: ${id}`)
    return entry
  }

  async execute(id, ctx, args) {
    return await this.get(id).execute(ctx, args)
  }

  getSources() {
    const sources = new Set()
    for (const entry of this.tools.values()) {
      if (entry.source !== 'builtin') sources.add(entry.source)
    }
    return sources
  }

  list() {
    return [...this.tools.values()].map(e => ({
      id: e.id,
      name: e.name,
      source: e.source,
      description: e.description,
      type: e.source === 'builtin' ? 'category' : 'worker',
    }))
  }
}
